import {
  GSAddressForm,
  GSGlobalCardElement,
  GSInfoGroup,
  GSQuickFilter,
  GSRadioGroup,
  GSToggle,
  defaultLayouts,
  defaultBorders
} from "golfstatus_react_components";
import PaymentOption from "../components/payment-option";
import RegistrationOrderSummary from "../components/registration-order-summary";
import StripeCard from "../components/stripe-card";
import {moneyWithCommas} from "../helpers/RegexHelper";
import CustomerInvoiceSummary from "../components/customer-invoice/customer-invoice-summary";

export const getPaymentFormSections = (context) => {
  if (context) {
    const paymentOptionSection = {
      type: "vertical large-gap",
      title: "Payment Method",
      fields: [
        {
          value: (
            <GSQuickFilter
              searchListType={"medium-large-gap"}
              selectedItem={context.selectedPaymentOption}
              filteredList={context.getPaymentOptions()}
              itemSelected={(item) => context.onSelectedPaymentOptionChange(item)}
              itemRemoved={() => context.onSelectedPaymentOptionChange(null)}
              itemStyle={{...defaultBorders.primaryBorder.themed(context.mode), ...defaultBorders.mediumBorderRadius}}
              getItem={(paymentOption) => (
                <PaymentOption {...paymentOption} />
              )}
            />
          ),
          customView: true,
          isEditable: true,
          required: true,
        },
      ],
    };

    const orderSummary = {
      type: "vertical large-gap",
      fields: [
        {
          value: (<RegistrationOrderSummary {...context} />),
          customView: true,
        },
      ],
    };

    const getInvoiceTotal = () => {
      const total = context?.getTotalCostWithFees();
      let groups = [
        {
          sections: [
            {
              sectionItems: [
                {
                  type: "primary no-wrap",
                  value: "Invoice Total",
                },
              ],
            },
          ],
        },
        {
          sections: [
            {
              sectionItems: [
                {
                  type: "primary no-wrap total",
                  value: moneyWithCommas(total),
                },
              ],
            },
          ],
        },
      ];

      return (
        <div style={context?.totalStyle} className={"total-container"}>
          <GSInfoGroup dataGroups={groups} listType={"total-info"} />
        </div>
      );
    };

    const totalBanner = {
      type: "no-pad",
      style: {...defaultLayouts.stickyBottom, ...defaultLayouts.stickyTop, zIndex: "1"},
      fields: [
        {
          value: (getInvoiceTotal()),
          customView: true,
        },
      ],
    };

    const cardDetails = {
      type: "vertical large-gap",
      title: "Card Details",
      fields: [
        {
          label: "Cardholder First Name",
          placeholder: "First Name",
          value: context.paymentDetails?.firstName,
          failedValidation: context?.validationFailed,
          onChange: (e) => context.onPaymentDetailsChange({...context.paymentDetails, firstName: e.target.value}),
          isEditable: true,
          required: true,
        },
        {
          label: "Cardholder Last Name",
          placeholder: "Last Name",
          value: context.paymentDetails?.lastName,
          failedValidation: context?.validationFailed,
          onChange: (e) => context.onPaymentDetailsChange({...context.paymentDetails, lastName: e.target.value}),
          isEditable: true,
          required: true,
        },
        {
          customView: true,
          value: context.details?.organization?.isEscrowAccount ? (
            <GSGlobalCardElement
              apiKey={process.env.REACT_APP_GLOBAL_KEY}
              environment={process.env.REACT_APP_GLOBAL_ENV}
              autoComplete={true}
              onSuccess={context.submitPayment}
              onError={(e) => console.log(e)} // TODO: provide an error handler?
              formValid={context.isValid}
              saveBtnQueryStr={"customer-invoice-pay gs-button.black.primary"}
              buttonText={"Submit Payment"}
            />
          ) : (
            <StripeCard onChange={context.onCreditCardChange} />
          ),
          isEditable: true,
          required: true,
        },
      ],
    };

    // only allow new saved payments for escrow accounts using Global
    if (context.details?.organization?.isEscrowAccount && context.isUserLoggedIn()) {
      cardDetails.fields.push({
        label: "Add Card To My Profile",
        value: (
          <GSToggle
            value={context.paymentDetails?.savePayment}
            label="Use this card for future purchases."
            trueDescription="Yes"
            falseDescription="No"
            onClick={() => context.onPaymentDetailsChange({...context.paymentDetails, savePayment: !context.paymentDetails?.savePayment})}
          />
        ),
        customView: true,
        isEditable: true,
      });
    }

    const billingAddress = {
      type: "vertical large-gap",
      title: "Billing Address",
      fields: [
        {
          customView: true,
          failedValidation: context.validationFailed,
          validation: {
            isValid: () => Boolean(context.billingAddress?.country && context.billingAddress?.address && context.billingAddress?.city && context.billingAddress?.state && context.billingAddress?.postal),
            invalidLabel: 'Must complete billing address.',
          },
          isEditable: true,
          label: "Billing Address *",
          required: true,
          value: (
            <GSAddressForm
              {...context.billingAddress}
              onChange={context.onBillingAddressChange}
              requiredFields={['country', 'address', 'city', 'state', 'postal']}
            />
          ),
        },
      ],
    };

    const accountTypeOptions = [
      { label: "Checking", value: "checking" },
      { label: "Savings", value: "savings" },
    ];

    const nameTypeOptions = [
      { label: "Business", value: "business" },
      { label: "Personal", value: "personal" },
    ];

    const validAccountNumber = () => {
      const {accountNumber,  confirmAccountNumber} = context.paymentDetails;
      return accountNumber && accountNumber === confirmAccountNumber;
    };

    const accountDetails = {
      type: "vertical large-gap",
      title: "Account Details",
      fields: [
        {
          label: "Account Type",
          value: (
            <GSRadioGroup
              selectedOption={accountTypeOptions.find((at) => at.value === context.paymentDetails?.accountType)}
              selectionChanged={(e) => context.onPaymentDetailsChange({...context.paymentDetails, accountType: e.value})}
              options={accountTypeOptions}
              isLtr={true}
            />
          ),
          failedValidation: context.validationFailed,
          customView: true,
          isEditable: true,
          required: true,
        },
        {
          label: "Name on Account",
          description: "Is this a business or personal account?",
          value: (
            <GSRadioGroup
              selectedOption={nameTypeOptions.find((at) => at.value === context.paymentDetails?.nameType)}
              selectionChanged={(e) => context.onPaymentDetailsChange({...context.paymentDetails, nameType: e.value})}
              options={nameTypeOptions}
              isLtr={true}
            />
          ),
          failedValidation: context.validationFailed,
          customView: true,
          isEditable: true,
          required: true,
        },
      ]
    };

    if (context.isBusiness()) {
      accountDetails.fields.push(...[
        {
          label: "Business Name",
          placeholder: "Business Name",
          value: context.paymentDetails?.businessName,
          failedValidation: context.validationFailed,
          onChange: (e) => context.onPaymentDetailsChange({...context.paymentDetails, businessName: e.target.value}),
          isEditable: true,
          required: true,
        },
      ]);
    } else if (context.isPersonal()) {
      accountDetails.fields.push(...[
        {
          label: "Account Holder First Name",
          placeholder: "First Name",
          value: context.paymentDetails?.firstName,
          failedValidation: context.validationFailed,
          onChange: (e) => context.onPaymentDetailsChange({...context.paymentDetails, firstName: e.target.value}),
          isEditable: true,
          required: true,
        },
        {
          label: "Account Holder Last Name",
          placeholder: "Last Name",
          value: context.paymentDetails?.lastName,
          failedValidation: context.validationFailed,
          onChange: (e) => context.onPaymentDetailsChange({...context.paymentDetails, lastName: e.target.value}),
          isEditable: true,
          required: true,
        },
      ]);
    }

    accountDetails.fields.push(...[
      {
        label: "Routing Number",
        placeholder: "e.g. 123456789",
        value: context.paymentDetails?.routingNumber,
        failedValidation: context.validationFailed,
        validation: {
          isValid: () => context.paymentDetails?.routingNumber?.length === 9,
          invalidLabel: 'Routing number must be 9 digits',
        },
        description: "Routing numbers must be 9 digits.",
        onChange: (e) => context.onPaymentDetailsChange({...context.paymentDetails, routingNumber: e.target.value}),
        isEditable: true,
        required: true,
      },
      {
        label: "Account Number",
        placeholder: "e.g. 1234567890",
        value: context.paymentDetails?.accountNumber,
        failedValidation: context.validationFailed,
        description: "Account numbers must be 17 digits or less.",
        onChange: (e) => context.onPaymentDetailsChange({...context.paymentDetails, accountNumber: e.target.value}),
        isEditable: true,
        required: true,
      },
      {
        label: "Confirm Account Number",
        placeholder: "e.g. 1234567890",
        value: context.paymentDetails?.confirmAccountNumber,
        validation: {
          isValid: validAccountNumber,
          invalidLabel: 'Account numbers do not match',
        },
        failedValidation: context.validationFailed,
        onChange: (e) => context.onPaymentDetailsChange({...context.paymentDetails, confirmAccountNumber: e.target.value}),
        isEditable: true,
        required: true,
      },
    ]);

    if (context.details?.organization?.isEscrowAccount && context.isUserLoggedIn()) {
      accountDetails.fields.push({
        label: "Add Account To My Profile",
        value: (
          <GSToggle
            value={context.paymentDetails?.savePayment}
            label="Use this account for future purchases."
            trueDescription="Yes"
            falseDescription="No"
            onClick={() => context.onPaymentDetailsChange({...context.paymentDetails, savePayment: !context.paymentDetails?.savePayment})}
          />
        ),
        customView: true,
        isEditable: true,
      });
    }

    const captcha = {
      type: 'horizontal',
      fields: [
        {
          fit: 'stretch',
          failedValidation: context.validationFailed,
          validation: {
            isValid: () => Boolean(context.captchaToken),
            invalidLabel: 'Must complete captcha.',
          },
          value: (context.getCaptcha),
          customView: true,
        },
      ],
    };

    let sections = [{ ...paymentOptionSection }, { ...orderSummary }, { ...totalBanner }];
    if (context.selectedPaymentOption) {
      if (context.selectedPaymentOption.saved) {
        sections.push({...captcha});
      } else {
        if (context.selectedPaymentOption.paymentType === "bank_account") {
          sections.push({...accountDetails}, {...captcha});
        } else {
          sections.push({...cardDetails}, {...billingAddress}, {...captcha});
        }
      }
    }

    return sections;
  }
};

export const getPaymentConfirmationFormSections = (context) => {
  const getInvoiceTotal = () => {
    const total = context.details?.registrationOrder?.purchaseCost || 0.00;
    let groups = [
      {
        sections: [
          {
            sectionItems: [
              {
                type: "primary no-wrap",
                value: "Invoice Total",
              },
            ],
          },
        ],
      },
      {
        sections: [
          {
            sectionItems: [
              {
                type: "primary no-wrap total",
                value: moneyWithCommas(total),
              },
            ],
          },
        ],
      },
    ];

    return (
      <div className={"total-container"}>
        <GSInfoGroup dataGroups={groups} listType={"total-info"} />
      </div>
    );
  };

  const getPaymentText = () => {
    if (context?.isACHPendingInvoice) {
      return (
        <div className="payment-text">
          It can take up to 7 business days for bank account transactions to
          clear. A receipt has been sent to{" "}
          {context?.details?.invoice?.contactEmail}. To help you identify this
          transaction, the charge will appear as:{" "}
          <b>GolfStatus Golf Event Lincoln USA</b>.
        </div>
      );
    }
    return (
      <div className="payment-text">
        This invoice has been paid! A receipt has been sent to{" "}
        {context?.details?.invoice?.contactEmail}. To help you identify this
        transaction, the charge will appear as:{" "}
        <b>GolfStatus Golf Event Lincoln USA</b>.
      </div>
    );
  };

  const getTotalSummary = () => {
    return (
      <div className="container">
        {getInvoiceTotal()}
        <CustomerInvoiceSummary {...context} />
      </div>
    );
  }

  const invoiceSummary = {
    type: "vertical large-gap",
    fields: [
      {
        value: (getPaymentText()),
        customView: true,
      },
      {
        value: (getTotalSummary()),
        customView: true,
      },
    ],
  };

  return [invoiceSummary];
};
